import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import CustomModal from "../ui/modal/modal.component";
import axios from "../../api/axios";
import { MY_LOCATION_ENDPOINT } from "../../consts";
import { useIsServiceRole } from "../../contexts/auth";

const LOCATION_NOTICE_ACKNOWLEDGED_KEY = "locationSharingNoticeAcknowledged";
// A fix is reported at most this often, unless the technician has moved
// further than MIN_REPORT_DISTANCE_M since the last report.
const MIN_REPORT_INTERVAL_MS = 60 * 1000;
const MIN_REPORT_DISTANCE_M = 100;
const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  maximumAge: 60 * 1000,
  timeout: 20 * 1000,
};

type ReportedPosition = { lat: number; lng: number; at: number };

const readNoticeAcknowledged = (): boolean => {
  try {
    return localStorage.getItem(LOCATION_NOTICE_ACKNOWLEDGED_KEY) === "true";
  } catch {
    return false;
  }
};

const storeNoticeAcknowledged = () => {
  try {
    localStorage.setItem(LOCATION_NOTICE_ACKNOWLEDGED_KEY, "true");
  } catch {
    // Storage may be unavailable (private mode) - the notice just shows again next time.
  }
};

const distanceMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};

/**
 * Reports a technician's position to the backend while the app is open, on
 * every page - so admins can see their last known location on the
 * technicians map. Renders nothing for non-service users.
 */
export const LocationTracker = () => {
  const { t } = useTranslation();
  const isServiceRole = useIsServiceRole();
  const [acknowledged, setAcknowledged] = useState<boolean>(readNoticeAcknowledged);
  const lastReportedRef = useRef<ReportedPosition | null>(null);
  const supported = typeof navigator !== "undefined" && !!navigator.geolocation;

  const reportPosition = useCallback((position: GeolocationPosition) => {
    const current = { lat: position.coords.latitude, lng: position.coords.longitude };
    const now = Date.now();
    const last = lastReportedRef.current;
    if (
      last &&
      now - last.at < MIN_REPORT_INTERVAL_MS &&
      distanceMeters(last, current) < MIN_REPORT_DISTANCE_M
    ) {
      return;
    }

    lastReportedRef.current = { ...current, at: now };
    axios
      .post(MY_LOCATION_ENDPOINT, {
        latitude: current.lat,
        longitude: current.lng,
        accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
      })
      .catch(() => {
        // Runs in the background on every page - a failed report is simply
        // retried with the next fix instead of bothering the technician.
        lastReportedRef.current = last;
      });
  }, []);

  useEffect(() => {
    if (!isServiceRole || !acknowledged || !supported) {
      return;
    }

    const watchId = navigator.geolocation.watchPosition(reportPosition, () => {}, GEOLOCATION_OPTIONS);

    // Mobile browsers pause watchPosition in the background; ask for a fresh
    // fix as soon as the technician comes back to the app.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        navigator.geolocation.getCurrentPosition(reportPosition, () => {}, GEOLOCATION_OPTIONS);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isServiceRole, acknowledged, supported, reportPosition]);

  if (!isServiceRole || !supported || acknowledged) {
    return null;
  }

  const acknowledge = () => {
    storeNoticeAcknowledged();
    setAcknowledged(true);
  };

  return (
    <CustomModal
      opened
      setOpened={(value) => {
        if (!value) acknowledge();
      }}
      title={t("Location sharing")}
      width={420}
      minWidth="min(420px, 90vw)"
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("Your location is shared with administrators while you use the app, so they can see where technicians are. Your browser will ask for permission next.")}
          </Typography>
          <Button variant="contained" onClick={acknowledge} fullWidth>
            {t("Understood")}
          </Button>
        </Box>
      }
    />
  );
};
